import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useAuth, formatApiErrorDetail } from "@/hooks/useAuth";
import { COUNTRY_LIST, rulesetForCountry } from "@/data/countryList";

export function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({
    email: "",
    username: "",
    password: "",
    country: "",
    date_of_birth: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const countryEntry = COUNTRY_LIST.find((c) => c.code === form.country);
      await register({
        email: form.email.trim(),
        username: form.username.trim(),
        password: form.password,
        country: countryEntry ? countryEntry.name : null,
        preferred_variant: rulesetForCountry(form.country),
        date_of_birth: form.date_of_birth || null,
      });
      navigate("/play");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Create your account</p>
        <h1 className="font-display text-4xl mt-1">Join Play Tsoro</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Already registered? <Link to="/login" className="text-primary hover:underline">Sign in</Link>
        </p>

        <Card className="mt-8 p-6">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="register-username"
                value={form.username}
                onChange={onChange("username")}
                placeholder="3–20 letters, numbers or _"
                required
                minLength={3}
                maxLength={20}
                autoComplete="username"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="register-email"
                type="email"
                value={form.email}
                onChange={onChange("email")}
                required
                autoComplete="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="register-password"
                type="password"
                value={form.password}
                onChange={onChange("password")}
                placeholder="At least 8 characters"
                minLength={8}
                required
                autoComplete="new-password"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="date_of_birth">Date of birth</Label>
              <Input
                id="date_of_birth"
                data-testid="register-dob"
                type="date"
                value={form.date_of_birth}
                onChange={onChange("date_of_birth")}
                required
                max={new Date().toISOString().slice(0, 10)}
              />
              <p className="text-[11px] text-muted-foreground">
                We use this to keep age-appropriate players in the right chat
                rooms. Under-13s play in a kids-safe zone with canned phrases
                only — no free-text chat. You can always invite a known friend
                by their exact username regardless of age.
              </p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="country">Country</Label>
              <Select
                value={form.country}
                onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}
              >
                <SelectTrigger id="country" data-testid="register-country">
                  <SelectValue placeholder="Choose your country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_LIST.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.country && (
                <p className="text-xs text-muted-foreground mt-1">
                  Your default variant: <span className="font-mono text-foreground/80">{rulesetForCountry(form.country)}</span>
                </p>
              )}
            </div>

            {error && (
              <p data-testid="register-error" className="text-sm text-destructive">
                {error}
              </p>
            )}
            <Button
              data-testid="register-submit"
              type="submit"
              disabled={busy}
              className="w-full rounded-full"
            >
              {busy ? "Creating account…" : "Create account"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              By signing up you agree to our <Link to="/terms" className="underline">terms</Link> and{" "}
              <Link to="/privacy" className="underline">privacy</Link>.
            </p>
          </form>
        </Card>
      </section>
    </Layout>
  );
}
